import { IPromise, IScope } from "angular";
import { ISlackConfig, ISlackChannel, ISlackConfigActivity } from "@gtmhub/automation/models/automation";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IntegrationService } from "@gtmhub/integrations/services";
import { AutomationService } from "@gtmhub/automation/services/automation";
import { IStateInit } from "@gtmhub/core/routing";
import { ICollection } from "@gtmhub/models";
import { IConfigActivity } from "@gtmhub/integrations/models";

export interface ISlackParams {
  error: string;
}

export interface ISlackScope extends IScope {
  button: string;
  set: boolean;
  workspace: string;
  config: ISlackConfig;
  error: string;
  indicators: {
    channels: boolean;
    config: boolean;
    saving: boolean;
  };
  selectingChannel: boolean;
  channelSearchInput: string;
  channels: ISlackChannel[];
  selectedChannel: ISlackChannel;
  checkedActivities: Record<string, boolean>;
  activities: ICollection<IConfigActivity>;
  saveSlackNotificationSettings(): void;
  changeChannelSettings(channel: ISlackChannel): void;
  toggleSelection(): void;
}

export class SlackCtrl implements IStateInit {
  public static $inject = ["$scope", "$stateParams", "AutomationService", "IntegrationService", "UIErrorHandlingService"];

  constructor(
    private $scope: ISlackScope,
    private $stateParams: ISlackParams,
    private automationService: AutomationService,
    private integrationService: IntegrationService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$scope.indicators = {
      channels: false,
      config: false,
      saving: false,
    };
    this.$scope.selectingChannel = false;
    this.$scope.channels = [];
    this.$scope.checkedActivities = {};
    this.$scope.saveSlackNotificationSettings = this.saveSlackNotificationSettings;
    this.$scope.changeChannelSettings = this.changeChannelSettings;
    this.$scope.toggleSelection = this.toggleSelection;

    if (typeof this.$stateParams.error !== "undefined") {
      this.$scope.error = this.$stateParams.error;
    }

    this.automationService.getSlackButton().then((button) => {
      this.$scope.button = button;
    });

    this.integrationService.getConfigActivities().then(
      (activities) => {
        this.$scope.activities = activities;
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators.channels = true;
    return this.automationService.getSlackChannels().then(
      (workspace) => {
        this.$scope.indicators.channels = false;
        this.$scope.workspace = workspace.workspace;

        if (workspace.channels.length > 0) {
          this.$scope.channels = workspace.channels;
          this.$scope.set = true;

          this.$scope.indicators.config = true;
          return this.integrationService.getConfig("slack").then(
            (config: ISlackConfig) => {
              this.$scope.indicators.config = false;
              this.$scope.config = config;
              this.$scope.selectedChannel = this.$scope.channels.find((channel) => channel.id === this.$scope.config.channel);
              this.$scope.channelSearchInput = "#" + this.$scope.selectedChannel.name;

              if (config.activities) {
                this.generateCheckedActivitiesModel(config.activities);
              }
            },
            (error) => {
              this.$scope.indicators.config = false;
              if (error.data === "config not found") {
                this.createNewSlackConfig();
              }
            }
          );
        }
      },
      (error) => {
        this.$scope.indicators.channels = false;
        if (error.data === "channels not found") {
          this.$scope.set = false;
        }
      }
    );
  };

  private generateCheckedActivitiesModel = (activities: ISlackConfigActivity): void => {
    this.$scope.checkedActivities = Object.entries(activities).reduce(
      (result, [activity, config]) => (config.enabled === true ? Object.assign(result, { [activity]: true }) : result),
      {}
    );
  };

  private changeChannelSettings = (channel: ISlackChannel): void => {
    this.$scope.selectedChannel = channel;
    this.$scope.channelSearchInput = "#" + channel.name;
    this.$scope.selectingChannel = false;
  };

  private toggleSelection = (): void => {
    this.$scope.selectingChannel = !this.$scope.selectingChannel;
  };

  private saveSlackNotificationSettings = (): void => {
    this.$scope.indicators.saving = true;
    const activities: ISlackConfigActivity = Object.entries(this.$scope.checkedActivities).reduce(
      (result, [activity, checked]) =>
        checked
          ? Object.assign(result, {
              [activity]: {
                display: this.getDisplayName(activity),
                enabled: true,
              },
            })
          : result,
      {}
    );

    this.integrationService.updateConfig({ activities, channel: this.$scope.selectedChannel.id }, "slack").then(() => {
      this.$scope.indicators.saving = false;
    });
  };

  private createNewSlackConfig = (): void => {
    const defaultChannel = this.$scope.channels.find((channel) => channel.name === "general");
    const config = {
      slack: {
        teamId: this.$scope.workspace,
        channel: defaultChannel ? defaultChannel.id : this.$scope.channels[0].id,
        activities: this.$scope.activities.items.reduce((activitiesMap, activity) => {
          activitiesMap[activity.name] = { display: activity.display, enabled: false };
          return activitiesMap;
        }, {}),
      },
    };

    this.integrationService.createConfig(config, "slack").then(
      (newConfig: ISlackConfig) => {
        this.$scope.indicators.config = false;
        this.$scope.config = newConfig;
        this.$scope.selectedChannel = this.$scope.channels.find((channel) => channel.id === this.$scope.config.channel);
        this.$scope.channelSearchInput = "#" + this.$scope.selectedChannel.name;

        if (newConfig.activities) {
          this.generateCheckedActivitiesModel(newConfig.activities);
        }
      },
      (error) => {
        this.$scope.indicators.config = false;
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  private getDisplayName = (key: string): string => {
    for (let i = 0; i < this.$scope.activities.items.length; i++) {
      if (this.$scope.activities.items[i].name === key) {
        return this.$scope.activities.items[i].display;
      }
    }
  };
}
